import formatPrice from '@honda-canada/js-utilities/lib/formatPrice';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Optional, useMediaQueries } from '@honda-canada/design-system-react';
import { InView } from 'react-intersection-observer';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AccessoryCard from './shared/AccessoryCard';
import AccessoryCardPanel from './AccessoryCardPanel';
import AccessoryConflictsModal from './shared/AccessoryConflictsModal';
import AccessoryDetailsModal from './AccessoryDetailsModal';
import Context from './service/Context';
import ExteriorColorsPanel from './ExteriorColorsPanel';
import InteriorColorsPanel from './InteriorColorsPanel';
import ProtectionCard from './shared/ProtectionCard';
import ProtectionExcludeModal from './shared/ProtectionExcludeModal';
import ProtectionPanel from './ProtectionPanel';
import ViewMoreAccessoriesPanel from './ViewMoreAccessoriesPanel';
import gtmAccessoriesSubcategoryClick from '../../core/gtm/gtmAccessoriesSubcategoryClick';
import gtmForGenericCtaLinkClick from '../../core/gtm/gtmForGenericCtaLinkClick';
import gtmProtectionsCategorySelect from '../../core/gtm/gtmProtectionsCategorySelect';
import isSSR from '../../utils/isSSR';
import themeStyles from './ConfigureBapStyles';
import useAccessoryAddRemove from './hooks/useAccessoryAddRemove';
import useFetchColors from './hooks/useFetchColors';
import useInventoryStatus from './hooks/useInventoryStatus';
import useScroll from './hooks/useScroll';
import useSyncBAPStateToURL from './hooks/useSyncBAPStateToURL';
import useVisibleSections from './hooks/useVisibleSections';
import { ACCESSORIES, CHARGING, EXTERIOR, INTERIOR, IN_VIEW_ROOT_MARGIN, IN_VIEW_STYLE, IN_VIEW_THRESHOLD, PROTECTION, WHEELS, } from './utils/constants';
import { EqualHeight } from '../../utils/components/EqualHeight';
import { clearAllConfigureBapToastNotification, setCurrentAccessoryModalId } from '../../core/reducers/ui';
import { selectBodyTypesForGtm, selectCurrentVehicleFromVehicles } from '../../core/reducers/vehicles';
import { selectCurrentVehicle } from '../../core/reducers/currentVehicle';
import { selectPrimaryCalculatorPaymentFrequency } from '../../core/reducers/financialCalculators';
import { useAppDispatch, useAppSelector } from '../../core/utils';
import { useLanguage } from '../../utils/sitecoreContext';
const EXTERIORCOLORS = 'exteriorcolors';
const INTERIORCOLORS = 'interiorcolors';
const MAX_ACCESSORY_CARDS_DISPLAYED = 8;
const ConfigureBapWrapper = themeStyles.apply(Box, 'ConfigureBapWrapper');
const ConfigureBapUI = ({ accessories, categoryOptions, defaultSubCategory, selectedSubCategory, setSelectedSubCategory, isAccessoryDataFailed, isAccessoryDataLoaded, protections, protectionOptions, selectedProtectionSubCategory, subcategoryLabels, protectionSubcategoryLabels, setProtectionSelectedSubCategory, defaultProtectionSubCategory, componentName, isFeaturedSubCategorySelected, }) => {
    var _a;
    const { accessoryTitlePb, setScrollPosition, setConfigurePreviousPosition } = useContext(Context);
    const dispatch = useAppDispatch();
    const language = useLanguage();
    const history = useHistory();
    const { isDesktop } = useMediaQueries();
    const { t } = useTranslation();
    const scrollRef = useRef(null);
    const [targetSlideIndexMap, setTargetSlideIndexMap] = useState({
        [CHARGING]: 0,
        [WHEELS]: 0,
        [ACCESSORIES]: 0,
    });
    const { activeProgressBarItem, currentViewMoreModalId } = useAppSelector(state => state.ui.ConfigureBap);
    const paymentFrequency = useAppSelector(selectPrimaryCalculatorPaymentFrequency);
    const paymentFrequencyLabel = t(`Shared.Common.${paymentFrequency}OptionLabel`);
    const allAccessoriesLabel = t('Pages.Bap.allAccessoriesLabel');
    const mySelectionsLabel = t('Pages.Bap.mySelectionsLabel');
    const featuredAccessoriesLabel = t('Pages.Bap.featuredLabel');
    const includedLabel = t('Pages.Bap.includedLabel');
    const updateSlideIndex = (category, newIndex) => {
        setTargetSlideIndexMap(prev => ({
            ...prev,
            [category]: newIndex,
        }));
    };
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    useSyncBAPStateToURL();
    const { accessoryKeys, modelYear } = useAppSelector(selectCurrentVehicle);
    const currentVehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    const { colors, isFetching, hasError, isUninitialized } = useFetchColors((_a = currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelSitecoreId) !== null && _a !== void 0 ? _a : '', modelYear !== null && modelYear !== void 0 ? modelYear : '');
    const { currentAccessoryModalId } = useAppSelector(state => state.ui.ConfigureBap);
    const formatPriceWithCurrentLanguage = useMemo(() => (price) => formatPrice(price, language, 2), [language]);
    const { inventoryStatus } = useInventoryStatus();
    const { pathname, search } = useLocation();
    useScroll({ scrollRef, currentAccessoryModalId, currentViewMoreModalId });
    useEffect(() => () => {
        dispatch(clearAllConfigureBapToastNotification());
    }, [dispatch]);
    const { itemsToAdd: accessoriesToAdd, itemsToRemove: accessoriesToRemove, isAdding: isAddingAccessories, selectedItem: selectedAccessory, showModal: showAccessoryConflictsModal, handleModalCancel: handleAccessoryConflictsModalCancel, handleModalConfirm: handleAccessoryConflictsModalConfirm, handleAccessorySelected, priceChange: accessoryPriceChange, summaryDescription, } = useAccessoryAddRemove(accessoryKeys, accessories || []);
    const categoryLabels = {
        [CHARGING]: t('Pages.Bap.chargingLabel'),
        [WHEELS]: t('Pages.Bap.wheelsLabel'),
        [ACCESSORIES]: t('Pages.Bap.accessoriesLabel'),
        [EXTERIORCOLORS]: t('Shared.Common.exteriorColoursLabel'),
        [INTERIORCOLORS]: t('Shared.Common.interiorColoursLabel'),
        [PROTECTION]: t('Pages.Bap.protectionLabel'),
    };
    const handleAccessoryLearnMoreClick = (businessKey, category, index) => {
        if (!isSSR()) {
            const previousScrollPosition = window.scrollY;
            if (setConfigurePreviousPosition) {
                setConfigurePreviousPosition(previousScrollPosition);
                const winScroll = document.body.scrollTop || document.documentElement.scrollTop;
                if (setScrollPosition) {
                    setScrollPosition(winScroll);
                }
            }
        }
        updateSlideIndex(category, index);
        dispatch(setCurrentAccessoryModalId(businessKey));
        const currentUrlParams = new URLSearchParams(window.location.search);
        history.push({
            pathname: window.location.pathname,
            search: currentUrlParams.toString(),
            hash: activeProgressBarItem,
        });
    };
    const learnMoreCtaText = t('Pages.Bap.learnMoreButton');
    const gtmPayload = {
        gtmBodyStyle,
        model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
        trim: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName,
        availabilityFlag: inventoryStatus,
    };
    const mapAccessoryItemToAccessoryCard = (accessory, i) => ({
        ...accessory,
        imageSrc: accessory === null || accessory === void 0 ? void 0 : accessory.thumbnailAssetUrl,
        imageAlt: accessory === null || accessory === void 0 ? void 0 : accessory.name,
        imageFallbackSrc: accessory === null || accessory === void 0 ? void 0 : accessory.defaultThumbnailAssetUrl,
        includedBadgeText: includedLabel,
        priceText: formatPriceWithCurrentLanguage(accessory === null || accessory === void 0 ? void 0 : accessory.provincialPrice),
        paymentText: `${formatPriceWithCurrentLanguage(accessory === null || accessory === void 0 ? void 0 : accessory.payment)}/${paymentFrequencyLabel}`,
        learnMoreCtaText,
        onLearnMoreClick: () => {
            var _a, _b;
            gtmForGenericCtaLinkClick({
                ...gtmPayload,
                ctaType: 'explore',
                eventName: 'bap_cta',
                linkText: 'learn more',
                componentType: componentName,
                customizeSection: (_a = accessory === null || accessory === void 0 ? void 0 : accessory.category) === null || _a === void 0 ? void 0 : _a.id,
            });
            if ((accessory === null || accessory === void 0 ? void 0 : accessory.businessKey) && ((_b = accessory === null || accessory === void 0 ? void 0 : accessory.category) === null || _b === void 0 ? void 0 : _b.id) && i !== undefined) {
                handleAccessoryLearnMoreClick(accessory === null || accessory === void 0 ? void 0 : accessory.businessKey, accessory.category.id, i);
            }
        },
        onSelect: handleAccessorySelected,
        maxTitleLength: isDesktop ? 39 : 36,
        titlePaddingBottom: accessoryTitlePb,
    });
    const getAccessoryCards = (categoryId) => (accessories !== null && accessories !== void 0 ? accessories : [])
        .filter(accessory => { var _a; return ((_a = accessory.category) === null || _a === void 0 ? void 0 : _a.id) === categoryId; })
        .filter(accessory => {
        var _a, _b;
        if (categoryId !== ACCESSORIES || selectedSubCategory === allAccessoriesLabel || !selectedSubCategory) {
            return true;
        }
        if (selectedSubCategory === mySelectionsLabel) {
            return accessory.isSelected;
        }
        if (selectedSubCategory === featuredAccessoriesLabel) {
            return accessory.isFeatured;
        }
        return (((_a = accessory === null || accessory === void 0 ? void 0 : accessory.subCategory) === null || _a === void 0 ? void 0 : _a.name) === selectedSubCategory || ((_b = accessory === null || accessory === void 0 ? void 0 : accessory.subCategory) === null || _b === void 0 ? void 0 : _b.id) === selectedSubCategory);
    })
        .sort((a, b) => ((a === null || a === void 0 ? void 0 : a.accessoryDisplayOrder) || 0) - ((b === null || b === void 0 ? void 0 : b.accessoryDisplayOrder) || 0))
        .map(mapAccessoryItemToAccessoryCard);
    const isProtection = currentViewMoreModalId === PROTECTION;
    const noProtectionsAndAccessories = (!protections || !(protections === null || protections === void 0 ? void 0 : protections.length)) && (!accessories || !(accessories === null || accessories === void 0 ? void 0 : accessories.length));
    const { handleInViewChange, scrollSectionIntoView } = useVisibleSections();
    function getGtmName(options, value) {
        const item = options.find((categoryItem) => categoryItem.value === value);
        return item ? item.gtmName : '';
    }
    function handleProtectionSelectedSubCategory(subCategory, doNotFireGtmEvent) {
        if (setProtectionSelectedSubCategory)
            setProtectionSelectedSubCategory(subCategory);
        const protectionCategoryGtmName = getGtmName(protectionOptions, subCategory);
        if (!doNotFireGtmEvent) {
            gtmProtectionsCategorySelect({
                ...gtmPayload,
                category: protectionCategoryGtmName,
            });
        }
    }
    function handleSelectedSubCategory(accessoryCategory, doNotFireGtmEvent) {
        if (setSelectedSubCategory)
            setSelectedSubCategory(accessoryCategory);
        const accessoryCategoryGtmName = getGtmName(categoryOptions, accessoryCategory);
        if (!doNotFireGtmEvent) {
            gtmAccessoriesSubcategoryClick({
                ...gtmPayload,
                accessoryCategory: accessoryCategoryGtmName,
            });
        }
    }
    function handleCloseAccessoriesPanel(sectionName) {
        // Hotfix. Too short on time for a more more robust solution
        if (!isSSR())
            window.history.replaceState({}, '', `${pathname}${search}#${sectionName}`);
        setTimeout(() => {
            scrollSectionIntoView(sectionName);
        }, 100);
    }
    useEffect(() => () => {
        handleProtectionSelectedSubCategory(defaultProtectionSubCategory);
        handleSelectedSubCategory(defaultSubCategory);
    }, []);
    return (React.createElement(ConfigureBapWrapper, { ref: scrollRef, isAutoHeight: noProtectionsAndAccessories, style: IN_VIEW_STYLE },
        React.createElement(Optional, { when: currentAccessoryModalId },
            React.createElement(AccessoryDetailsModal, null)),
        React.createElement(Optional, { when: !currentAccessoryModalId },
            React.createElement(Optional, { when: currentViewMoreModalId },
                React.createElement(EqualHeight, null,
                    React.createElement(ViewMoreAccessoriesPanel, { isProtection: isProtection, setSelectedSubCategory: isProtection ? handleProtectionSelectedSubCategory : handleSelectedSubCategory, selectedSubCategory: isProtection ? selectedProtectionSubCategory : selectedSubCategory, defaultSubCategory: isProtection ? protectionSubcategoryLabels.ALL_PROTECTIONS : subcategoryLabels.ALL_ACCESSORIES, categoryOptions: isProtection ? protectionOptions : categoryOptions, label: categoryLabels[currentViewMoreModalId], gtmPayload: { ...gtmPayload, availabilityFlag: inventoryStatus }, componentName: componentName, customizeSection: isProtection ? PROTECTION : ACCESSORIES, isFeaturedSubCategorySelected: isFeaturedSubCategorySelected, onClose: () => handleCloseAccessoriesPanel(isProtection ? PROTECTION : ACCESSORIES) },
                        React.createElement(Optional, { when: isProtection }, protections === null || protections === void 0 ? void 0 : protections.map(({ key, title, ...others }) => (React.createElement(ProtectionCard, { key: key, id: key, name: title, ...others })))),
                        React.createElement(Optional, { when: !isProtection }, getAccessoryCards(ACCESSORIES).map(({ businessKey, ...others }) => (React.createElement(AccessoryCard, { businessKey: businessKey, ...others, key: businessKey }))) || null)))),
            React.createElement(Optional, { when: !currentViewMoreModalId },
                React.createElement(InView, { as: "div", onChange: inView => handleInViewChange(inView, EXTERIOR), id: `${EXTERIOR}-section`, rootMargin: IN_VIEW_ROOT_MARGIN, style: IN_VIEW_STYLE, threshold: IN_VIEW_THRESHOLD },
                    React.createElement(ExteriorColorsPanel, { id: `${EXTERIOR}-title`, categoryId: EXTERIORCOLORS, label: categoryLabels.exteriorcolors, colors: colors, isFetching: isFetching || isUninitialized, hasColorError: hasError, componentName: componentName })),
                React.createElement(AccessoryCardPanel, { categoryId: WHEELS, label: categoryLabels.wheels, accessoryCards: getAccessoryCards(WHEELS), targetSlideIndex: targetSlideIndexMap[WHEELS], handleInViewChange: handleInViewChange, showErrorMessage: isAccessoryDataFailed, showLoader: !isAccessoryDataLoaded && !isAccessoryDataFailed, maxCardsDisplayed: MAX_ACCESSORY_CARDS_DISPLAYED, defaultSubCategory: defaultSubCategory }),
                React.createElement(InView, { as: "div", onChange: inView => handleInViewChange(inView, INTERIOR), id: `${INTERIOR}-section`, rootMargin: IN_VIEW_ROOT_MARGIN, style: IN_VIEW_STYLE, threshold: IN_VIEW_THRESHOLD },
                    React.createElement(InteriorColorsPanel, { id: `${INTERIOR}-title`, categoryId: INTERIORCOLORS, label: categoryLabels.interiorcolors, colors: colors, isFetching: isFetching || isUninitialized, hasColorError: hasError })),
                React.createElement(AccessoryCardPanel, { categoryId: CHARGING, label: categoryLabels.charging, accessoryCards: getAccessoryCards(CHARGING), targetSlideIndex: targetSlideIndexMap[CHARGING], handleInViewChange: handleInViewChange, showErrorMessage: isAccessoryDataFailed, showLoader: !isAccessoryDataLoaded && !isAccessoryDataFailed, maxCardsDisplayed: MAX_ACCESSORY_CARDS_DISPLAYED, defaultSubCategory: defaultSubCategory, shouldSectionBeHidden: true }),
                React.createElement(AccessoryCardPanel, { categoryId: ACCESSORIES, label: categoryLabels.accessories, componentName: componentName, tooltipText: t('Pages.Bap.accessoriesTooltipText'), viewMoreLabel: t('Pages.Bap.viewMoreAccessoriesLabel'), accessoryCards: getAccessoryCards(ACCESSORIES), setSelectedSubCategory: handleSelectedSubCategory, selectedSubCategory: selectedSubCategory, categoryOptions: categoryOptions, targetSlideIndex: targetSlideIndexMap[ACCESSORIES], handleInViewChange: handleInViewChange, showErrorMessage: isAccessoryDataFailed, showLoader: !isAccessoryDataLoaded && !isAccessoryDataFailed, defaultSubCategory: defaultSubCategory, maxCardsDisplayed: MAX_ACCESSORY_CARDS_DISPLAYED }),
                React.createElement(ProtectionPanel, { categoryId: PROTECTION, componentName: componentName, handleInViewChange: handleInViewChange, protectionOptions: protectionOptions, protections: protections, selectedProtectionSubCategory: selectedProtectionSubCategory, defaultProtectionSubCategory: protectionSubcategoryLabels.ALL_PROTECTIONS, setProtectionSelectedSubCategory: handleProtectionSelectedSubCategory, isFeaturedSubCategorySelected: isFeaturedSubCategorySelected }))),
        React.createElement(ProtectionExcludeModal, { componentName: componentName }),
        React.createElement(AccessoryConflictsModal, { show: showAccessoryConflictsModal, title: t('Pages.Bap.configChangeHeading'), summaryDescription: summaryDescription, selectedItem: selectedAccessory && {
                ...mapAccessoryItemToAccessoryCard(selectedAccessory),
                hideLearnMoreCta: true,
                hideSelectIcon: true,
            }, selectionTitle: t('Pages.Bap.yourSelectionLabel'), itemsToChange: accessoriesToAdd.length
                ? accessoriesToAdd
                    .map(mapAccessoryItemToAccessoryCard)
                    .map(item => ({ ...item, hideLearnMoreCta: true, hideSelectIcon: true }))
                : accessoriesToRemove
                    .map(mapAccessoryItemToAccessoryCard)
                    .map(item => ({ ...item, hideLearnMoreCta: true, hideSelectIcon: true })), itemsToChangeTitle: isAddingAccessories && !accessoriesToRemove.length
                ? t('Pages.Bap.itemsToBeAddedLabel')
                : t('Pages.Bap.itemsToBeRemovedLabel'), priceChangeLabel: isAddingAccessories && !accessoriesToRemove.length
                ? t('Pages.Bap.priceChangeAddLabel')
                : t('Pages.Bap.priceChangeRemoveLabel'), priceChangeValue: `${accessoryPriceChange >= 0 ? '+' : ''}${formatPriceWithCurrentLanguage(accessoryPriceChange)}`, primaryButtonText: isAddingAccessories && !accessoriesToRemove.length ? t('Pages.Bap.okButton') : t('Pages.Bap.removeButton'), onCancel: handleAccessoryConflictsModalCancel, onConfirm: handleAccessoryConflictsModalConfirm, conflictType: isAddingAccessories && !accessoriesToRemove.length ? 'inclusion' : 'exclusion', componentName: componentName })));
};
export default ConfigureBapUI;
